import { HorsLigneError } from '../api/client.js'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  InputBase,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import PersonIcon from '@mui/icons-material/PersonOutline'
import AddIcon from '@mui/icons-material/Add'
import { globalSearch, type SearchResult } from '../features/customers/api.js'
import { useSessionStore } from '../stores/sessionStore.js'

/**
 * Palette de commandes (W-05) — Ctrl/Cmd + K.
 *
 * C'est la réponse concrète à l'objectif « trouver l'information en moins de
 * 3 clics » (§A1) : un raccourci, une frappe, Entrée. Elle mélange délibérément
 * résultats ET actions, pour éviter d'avoir à choisir entre « chercher » et
 * « faire » avant même de savoir ce qu'on cherche.
 */

const TYPE_LABELS: Record<string, string> = {
  customer: 'Clients',
  item: 'Articles',
  transaction: 'Transactions',
  supplier: 'Fournisseurs',
  appointment: 'Rendez-vous',
  task: 'Tâches',
  document: 'Documents',
}

interface Entry {
  kind: 'result' | 'action'
  type: string
  id: string
  title: string
  subtitle: string | null
  onSelect: () => void
}

export function CommandPalette(): JSX.Element {
  const navigate = useNavigate()
  const { slug } = useParams<{ slug: string }>()
  const store = useSessionStore((s) => (slug ? s.storeBySlug(slug) : undefined))

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [groups, setGroups] = useState<{ type: string; results: SearchResult[] }[]>([])
  const [took, setTook] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  /**
   * ⚠️ La recherche globale interroge le SERVEUR. Hors ligne, l'appel
   * échouait en silence et le panneau annonçait « Aucun résultat » : un
   * mensonge, qui laissait croire que la boutique ne contenait rien de tel.
   */
  const [horsLigne, setHorsLigne] = useState(false)
  const [active, setActive] = useState(0)
  const listRef = useRef<HTMLUListElement>(null)

  // Ctrl+K / Cmd+K depuis n'importe où dans l'application.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen((v) => !v)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Anti-rebond 150 ms (§7.7) : assez court pour paraître instantané, assez
  // long pour ne pas lancer une requête par caractère.
  useEffect(() => {
    if (!store || query.trim().length === 0) {
      setGroups([])
      setTook(null)
      return
    }
    const controller = new AbortController()
    const timer = setTimeout(() => {
      setLoading(true)
      globalSearch(store.storeId, query.trim(), controller.signal)
        .then((r) => {
          setGroups(r.groups)
          setTook(r.took)
          setActive(0)
          setHorsLigne(false)
        })
        .catch((e: unknown) => {
          // Une annulation (frappe suivante) n'est pas une panne.
          if (controller.signal.aborted) return
          setGroups([])
          setHorsLigne(e instanceof HorsLigneError)
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false)
        })
    }, 150)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [query, store])

  // Liste à plat des entrées navigables : résultats d'abord, actions ensuite.
  const entries: Entry[] = [
    ...groups.flatMap((g) =>
      g.results.map<Entry>((r) => ({
        kind: 'result',
        type: g.type,
        id: r.id,
        title: r.title,
        subtitle: r.subtitle,
        onSelect: () => {
          if (g.type === 'customer') navigate(`/s/${slug}/clients/${r.id}`)
        },
      })),
    ),
  ]

  if (query.trim().length >= 2) {
    entries.push({
      kind: 'action',
      type: 'action',
      id: 'create-customer',
      title: `Créer un client « ${query.trim()} »`,
      subtitle: null,
      onSelect: () => navigate(`/s/${slug}/clients?nouveau=${encodeURIComponent(query.trim())}`),
    })
  }

  const close = useCallback(() => {
    setOpen(false)
    setQuery('')
    setGroups([])
  }, [])

  function onKeyDown(e: React.KeyboardEvent): void {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((i) => Math.min(i + 1, entries.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const entry = entries[active]
      if (entry) {
        entry.onSelect()
        close()
      }
    }
  }

  // Maintient l'entrée active visible quand on navigue au clavier.
  useEffect(() => {
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' })
  }, [active])

  let index = -1

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="sm"
      slotProps={{ paper: { sx: { borderRadius: 3, mt: '10vh', alignSelf: 'flex-start' } } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', px: 5, py: 3 }}>
        <SearchIcon sx={{ color: 'text.secondary', mr: 3 }} />
        <InputBase
          autoFocus
          fullWidth
          placeholder="Rechercher un client, un produit, une facture…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          inputProps={{ 'aria-label': 'Recherche globale' }}
          sx={{ fontSize: 16 }}
        />
        {loading && <CircularProgress size={16} sx={{ ml: 2 }} />}
      </Box>

      <Divider />

      <Box sx={{ maxHeight: '55vh', overflowY: 'auto' }}>
        {query.trim() === '' ? (
          <Box sx={{ p: 8, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Tapez pour chercher dans les clients, articles, ventes et documents.
            </Typography>
          </Box>
        ) : entries.length === 0 && !loading ? (
          <Box sx={{ p: 8, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {horsLigne
                ? 'Recherche indisponible hors ligne : elle interroge le serveur. Les listes Clients et Catalogue, elles, restent consultables.'
                : `Aucun résultat pour « ${query} ».`}
            </Typography>
          </Box>
        ) : (
          <List ref={listRef} dense disablePadding>
            {groups.map((group) => (
              <Box key={group.type}>
                <Typography
                  variant="caption"
                  sx={{
                    px: 5,
                    pt: 4,
                    pb: 1,
                    display: 'block',
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {TYPE_LABELS[group.type] ?? group.type} · {group.results.length}
                </Typography>
                {group.results.map((r) => {
                  index += 1
                  const i = index
                  return (
                    <ListItemButton
                      key={r.id}
                      data-active={i === active}
                      selected={i === active}
                      onMouseEnter={() => setActive(i)}
                      onClick={() => {
                        entries[i]?.onSelect()
                        close()
                      }}
                      sx={{ px: 5 }}
                    >
                      <PersonIcon sx={{ fontSize: 18, mr: 3, color: 'text.secondary' }} />
                      <ListItemText primary={r.title} secondary={r.subtitle} />
                    </ListItemButton>
                  )
                })}
              </Box>
            ))}

            {query.trim().length >= 2 && (
              <>
                <Typography
                  variant="caption"
                  sx={{
                    px: 5,
                    pt: 4,
                    pb: 1,
                    display: 'block',
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Actions
                </Typography>
                {(() => {
                  index += 1
                  const i = index
                  return (
                    <ListItemButton
                      data-active={i === active}
                      selected={i === active}
                      onMouseEnter={() => setActive(i)}
                      onClick={() => {
                        entries[i]?.onSelect()
                        close()
                      }}
                      sx={{ px: 5 }}
                    >
                      <AddIcon sx={{ fontSize: 18, mr: 3, color: 'text.secondary' }} />
                      <ListItemText primary={`Créer un client « ${query.trim()} »`} />
                    </ListItemButton>
                  )
                })()}
              </>
            )}
          </List>
        )}
      </Box>

      <Divider />
      <Box
        sx={{
          px: 5,
          py: 2,
          display: 'flex',
          gap: 4,
          alignItems: 'center',
          color: 'text.secondary',
        }}
      >
        <Typography variant="caption">↑↓ naviguer</Typography>
        <Typography variant="caption">⏎ ouvrir</Typography>
        <Typography variant="caption">Échap fermer</Typography>
        <Box sx={{ flex: 1 }} />
        {took !== null && <Chip size="small" variant="outlined" label={`${took} ms`} />}
      </Box>
    </Dialog>
  )
}
